import { makeRequest } from "./instance";

// ============ AUTH APIs ============
export const loginAdmin = (data) => makeRequest("POST", "auth/login", data);
// Send an empty object: with no body the request carries the text "null",
// which the server's JSON parser rejects before logout runs.
export const logoutAdmin = () => makeRequest("POST", "auth/logout", {});
export const getAdminProfile = () => makeRequest("GET", "auth/me");
export const updateAdminProfile = (data) =>
  makeRequest("PATCH", "users/me", data);
export const changePassword = (data) =>
  makeRequest("PATCH", "users/me/password", data);
// ============ COUNTRIES APIs ============
export const getAllCountries = () =>
  makeRequest("GET", "countries/country/get-all");

// ============ CITIES APIs ============
export const getCitiesByCountry = (countryId) =>
  makeRequest("GET", `cities/city/get-all?countryId=${countryId}`);
// Single-city fetch — returns { id, name, countryId }.
// Needed so the mall edit screen can derive the parent country from a cityId
// (the malls API doesn't include countryId in its rows).
export const getCityById = (id) =>
  makeRequest("GET", `cities/city/get/${id}`);
// ============ MALLS APIs ============
// Accepts either:
//   - a string cityId (legacy)
//   - or an object { cityId, countryId } — countryId-only filter is supported
//     for browsing all malls in a country before a city is picked.
export const getMalls = (filter) => {
  let cityId, countryId;
  if (typeof filter === "string") {
    cityId = filter;
  } else if (filter && typeof filter === "object") {
    cityId = filter.cityId;
    countryId = filter.countryId;
  }
  const qs = new URLSearchParams();
  if (cityId) qs.set("cityId", cityId);
  if (countryId) qs.set("countryId", countryId);
  const suffix = qs.toString();
  return makeRequest(
    "GET",
    suffix ? `/malls/mall/get-all?${suffix}` : `/malls/mall/get-all`,
  );
};
// Path matches backend route mall.routes.ts:173.
export const getMallById = (id) =>
  makeRequest("GET", `malls/mall/get/${id}`);
export const createMall = (data) => makeRequest("POST", "malls/mall/create", data);
// Backend uses PATCH (mall.routes.ts:309), not PUT.
export const updateMall = (id, data) =>
  makeRequest("PATCH", `malls/mall/update/${id}`, data);
export const deleteMall = (id) => makeRequest("DELETE", `malls/mall/delete/${id}`);

// ============ RESTAURANTS APIs ============
// Uses the system-wide endpoint so the admin's default view (no filters)
// returns every restaurant. mallId / cityId / countryId / category are all
// optional and compose freely. The legacy mall-specific endpoint
// /restaurant/get-all/:mallId is reserved for customer reads (which apply an
// active-subscription filter the admin shouldn't see).
//
// Backwards-compat: accepts either
//   getRestaurants(mallId, { page, limit, category })   (legacy positional)
// or
//   getRestaurants({ mallId, cityId, countryId, category, page, limit })
export const getRestaurants = (first, params = {}) => {
  let filter = {};
  if (typeof first === "string") {
    filter = { ...params, mallId: first };
  } else if (first && typeof first === "object") {
    filter = first;
  }
  const qs = new URLSearchParams();
  if (filter.page) qs.append("page", filter.page);
  if (filter.limit) qs.append("limit", filter.limit);
  if (filter.mallId) qs.append("mallId", filter.mallId);
  if (filter.cityId) qs.append("cityId", filter.cityId);
  if (filter.countryId) qs.append("countryId", filter.countryId);
  if (filter.category) qs.append("category", filter.category);
  const queryString = qs.toString();
  return makeRequest(
    "GET",
    `restaurants/all${queryString ? `?${queryString}` : ""}`,
  );
};
export const getRestaurantById = (id) =>
  makeRequest("GET", `restaurant/get-details/${id}`);
export const createRestaurant = (data) =>
  makeRequest("POST", "admin/restaurants/create", data);
export const updateRestaurant = (id, data) =>
  makeRequest("PATCH", `restaurant/update/${id}`, data);
export const deleteRestaurant = (id) =>
  makeRequest("DELETE", `restaurant/delete/${id}`);
// Block / unblock a restaurant. Body shape matches the backend route
// PATCH /admin/restaurants/:restaurantId/block.
//   { isBlocked: true,  reason?: string } -> sets RestaurantStatus = "BLOCKED"
//   { isBlocked: false, reason?: string } -> sets RestaurantStatus = "ACTIVE"
// When BLOCKED, customer-facing reads hide the restaurant entirely (it
// vanishes from the user app's mall list, search, menu, etc.).
export const setRestaurantBlockStatus = (id, isBlocked, reason) =>
  makeRequest("PATCH", `admin/restaurants/${id}/block`, {
    isBlocked,
    ...(reason ? { reason } : {}),
  });

// PATCH /admin/restaurants/:id/approval — set approval state.
//   approvalStatus: "APPROVED" | "REJECTED" | "PENDING"
// New restaurants sign up as PENDING. Until admin sets APPROVED here,
// the customer-facing filter (activeSubscriptionWhere) hides them even
// if their subscription is active.
export const setRestaurantApprovalStatus = (id, approvalStatus, reason) =>
  makeRequest("PATCH", `admin/restaurants/${id}/approval`, {
    approvalStatus,
    ...(reason ? { reason } : {}),
  });

// ============ USERS APIs ============
export const getActiveUsers = () => makeRequest("GET", "admin/users/active");
export const getBlockedUsers = () => makeRequest("GET", "admin/users/blocked");
export const activateUser = (id, data) =>
  makeRequest("PATCH", `admin/users/${id}/activate`, data);
export const blockUser = (id, data) =>
  makeRequest("PATCH", `admin/users/${id}/block`, data);
// Optionally, keep create/update/delete if needed for admin user management
export const getUserById = (id) => makeRequest("GET", `users/${id}`);
export const createUser = (data) => makeRequest("POST", "users", data);
export const updateUser = (id, data) => makeRequest("PUT", `users/${id}`, data);
export const deleteUser = (id) => makeRequest("DELETE", `users/${id}`);

// ============ MALL ADMIN APIs (GAP-018, Super Admin only) ============
export const getMallAdmins = () => makeRequest("GET", "admin/mall-admins");
export const createMallAdmin = (data) =>
  makeRequest("POST", "admin/mall-admins", data);

// ============ ORDERS APIs ============
// Every order (a mall admin gets their mall's only). Read-only: shops run their
// orders from the app. params: status (NEW, IN_PROGRESS, DELIVERED, CANCELLED),
// mallId, restaurantId, search, from, to (Oman dates), page, limit.
export const getAdminOrders = (params) =>
  makeRequest("GET", "admin/orders", null, { params });
export const getAdminOrder = (id) => makeRequest("GET", `admin/orders/${id}`);

// ============ PROMO CODES APIs ============
export const getPromoCodes = () => makeRequest("GET", "admin/promo-codes");
export const getPromoCodeById = (id) =>
  makeRequest("GET", `admin/promo-codes/${id}`);
export const createPromoCode = (data) =>
  makeRequest("POST", "admin/promo-codes", data);
export const updatePromoCode = (id, data) =>
  makeRequest("PUT", `admin/promo-codes/${id}`, data);
export const deletePromoCode = (id) =>
  makeRequest("DELETE", `admin/promo-codes/${id}`);
// Image uploads happen on the same endpoint that creates/updates the resource
// (multipart/form-data with the relevant field name, e.g. "banner"). There is
// no standalone /upload/image route on the backend, so don't add helpers
// pointing at one.

export const getMallCusines = (mallId) =>
  makeRequest("GET", `cuisine/get-all/${mallId}`);

// The shop categories (food and drink, and shopping) the apps use:
// { cuisines: string[], groups: [{ name, categories: string[] }] }.
export const getShopCategories = () => makeRequest("GET", "cuisines/list");

// Shops' category suggestions, and categories added from the console
// (super admin only).
export const getCategoryRequests = () => makeRequest("GET", "admin/category-requests");
export const addCategoryRequest = (id, data) =>
  makeRequest("POST", `admin/category-requests/${id}/add`, data);
export const declineCategoryRequest = (id) =>
  makeRequest("POST", `admin/category-requests/${id}/decline`, {});
export const createShopCategory = (data) => makeRequest("POST", "admin/shop-categories", data);
export const removeShopCategory = (id) => makeRequest("DELETE", `admin/shop-categories/${id}`);

export const getAnalyticsData = () =>
  makeRequest("GET", "analytics/overall");

// Build the query string so undefined / null params are simply omitted —
// the backend defaults `period` to "all" and ignores missing dates, which
// is what we want when the caller passes nothing. Sending `period=undefined`
// (the old, buggy behaviour was even worse: `period${period}` with no `=`)
// makes the zod parse fail with 400.
export const getMallAnalytics = (mallId, period, startDate, endDate) => {
  const qs = new URLSearchParams();
  if (period) qs.set("period", period);
  if (startDate) qs.set("startDate", startDate);
  if (endDate) qs.set("endDate", endDate);
  const suffix = qs.toString();
  return makeRequest(
    "GET",
    suffix
      ? `analytics/mall/${mallId}?${suffix}`
      : `analytics/mall/${mallId}`
  );
};

// Vendor subscriptions (super admin). The vendor app sells nothing, so
// MallBuddy starts a vendor's subscription here or moves its end date.
export const getRestaurantSubscriptions = (restaurantId) =>
  makeRequest("GET", `subscriptions/list/${restaurantId}`);
export const getSubscriptionPlans = () => makeRequest("GET", "subscription-plans");
export const setSubscriptionEndDate = (id, endDate) =>
  makeRequest("PATCH", `subscriptions/${id}/end-date`, { endDate });
export const grantSubscription = (data) => makeRequest("POST", "subscriptions/grant", data);
// Every shop's plan in one list (super admin). params: state (ACTIVE, ENDING,
// PAST_DUE, ENDED, NONE, ENDS_SOON), mallId.
export const getSubscriptionsOverview = (params) =>
  makeRequest("GET", "admin/subscriptions", null, { params });

export default {
  getAnalyticsData,

  // Auth
  loginAdmin,
  logoutAdmin,
  getAdminProfile,
  updateAdminProfile,
  changePassword,

  // Malls
  getMalls,
  getMallById,
  createMall,
  updateMall,
  deleteMall,

  //Cuisines
  getMallCusines,

  // Restaurants
  getRestaurants,
  getRestaurantById,
  createRestaurant,
  updateRestaurant,
  deleteRestaurant,

  getUserById,
  createUser,
  updateUser,
  deleteUser,

  // Orders
  getAdminOrders,
  getAdminOrder,

  // Promo Codes
  getPromoCodes,
  getPromoCodeById,
  createPromoCode,
  updatePromoCode,
  deletePromoCode,
};
